// src/components/Address/AddressModal.jsx
import React, { useState, useEffect } from 'react';
import { addressService } from '../../services/addressService';

const AddressModal = ({ isOpen, onClose, onSuccess, initialData = null }) => {
  const [formData, setFormData] = useState({
    receiverName: '',
    phoneNumber: '',
    alternatePhoneNumber: '',
    pinCode: '',
    houseNumber: '',
    apartmentName: '',
    streetName: '',
    landMark: '',
    city: '',
    state: '',
    latitude: 0,
    longitude: 0,
    addressType: 'HOME', // Default matching backend
    isPrimary: false
  });

  const [loading, setLoading] = useState(false);
  const [fetchingPincode, setFetchingPincode] = useState(false);
  const [fetchingLocation, setFetchingLocation] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormData(initialData);
      } else {
        setFormData({
          receiverName: '', phoneNumber: '', alternatePhoneNumber: '', pinCode: '',
          houseNumber: '', apartmentName: '', streetName: '', landMark: '',
          city: '', state: '', latitude: 0, longitude: 0, addressType: 'HOME', isPrimary: false
        });
      }
      setError('');
    }
  }, [isOpen, initialData]);

  // Auto Fetch City/State when Pincode is 6 digits
  useEffect(() => {
    const fetchPincodeDetails = async () => {
      if (formData.pinCode.length === 6 && (!initialData || formData.pinCode !== initialData.pinCode)) {
        setFetchingPincode(true);
        try {
          const res = await addressService.getByPincode(formData.pinCode);
          if (res) {
            setFormData(prev => ({
              ...prev,
              city: res.city || prev.city,
              state: res.state || prev.state,
              streetName: prev.streetName || res.street || '',
              latitude: res.latitude || prev.latitude || 0,
              longitude: res.longitude || prev.longitude || 0
            }));
          }
        } catch (err) {
          console.log("Could not fetch location for pincode");
        } finally {
          setFetchingPincode(false);
        }
      }
    };
    fetchPincodeDetails();
  }, [formData.pinCode, initialData]);

  // Use Current Location Logic
  const handleCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }
    setFetchingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`);
          const data = await res.json();
          if (data && data.address) {
            const place = data.address;
            const city = place.city || place.town || place.village || place.county || '';
            const state = place.state || '';
            const pincode = place.postcode || '';
            const street = place.road || place.suburb || place.neighbourhood || '';

            setFormData(prev => ({
              ...prev,
              city: city,
              state: state,
              pinCode: pincode,
              streetName: prev.streetName || street,
              latitude: latitude,
              longitude: longitude
            }));
          }
        } catch (error) {
          alert('Could not fetch address details automatically.');
        } finally {
          setFetchingLocation(false);
        }
      },
      (error) => {
        setFetchingLocation(false);
        alert('Please allow location access in your browser to fetch current location.');
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 }
    );
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.receiverName || !formData.phoneNumber || !formData.pinCode || !formData.houseNumber || !formData.streetName) {
      setError("Please fill all required fields marked with *");
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const payload = { ...formData };
      if (initialData?.id) {
        await addressService.update(initialData.id, payload);
      } else {
        await addressService.add(payload);
      }
      onSuccess();
      onClose();
    } catch (err) {
      setError("Failed to save address. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-3xl w-full max-w-lg overflow-hidden shadow-2xl flex flex-col max-h-[90vh] animate-fade-in">
        
        {/* Header */}
        <div className="flex justify-between items-center p-5 md:p-6 border-b border-gray-100 bg-gray-50">
          <h2 className="text-xl font-black text-[#0F2F63]">
            {initialData ? 'Edit Address' : 'Add New Address'}
          </h2>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-gray-500 hover:text-red-500 shadow-sm transition-colors">
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Form Body */}
        <div className="p-5 md:p-6 overflow-y-auto flex-1 custom-scrollbar">
          {error && <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm font-bold rounded-xl border border-red-100"><i className="fas fa-exclamation-circle mr-2"></i>{error}</div>}
          
          {/* Current Location Button */}
          <div className="mb-5">
            <button 
              type="button" 
              onClick={handleCurrentLocation}
              disabled={fetchingLocation}
              className="w-full flex items-center justify-center py-3.5 bg-sky-50 text-sky-600 hover:bg-sky-100 rounded-xl font-bold transition-all gap-2 border border-sky-100"
            >
              <i className={`fas ${fetchingLocation ? 'fa-spinner fa-spin' : 'fa-location-arrow'}`}></i>
              {fetchingLocation ? 'Fetching your location...' : 'Use Current Location'}
            </button>
          </div>

          <form id="address-form" onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 ml-1">Receiver Name *</label>
                <input type="text" name="receiverName" value={formData.receiverName} onChange={handleChange} className="w-full px-4 py-3 rounded-xl border bg-gray-50 border-gray-200 text-gray-900 focus:border-[#FF7A59] focus:ring-2 focus:ring-[#FF7A59]/20 outline-none transition-all font-semibold" placeholder="Full Name" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 ml-1">Phone Number *</label>
                <input type="tel" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} maxLength="10" className="w-full px-4 py-3 rounded-xl border bg-gray-50 border-gray-200 text-gray-900 focus:border-[#FF7A59] focus:ring-2 focus:ring-[#FF7A59]/20 outline-none transition-all font-semibold" placeholder="10-digit mobile number" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 ml-1">Pincode * {fetchingPincode && <i className="fas fa-spinner fa-spin text-[#FF7A59] ml-1"></i>}</label>
                <input type="text" name="pinCode" value={formData.pinCode} onChange={handleChange} maxLength="6" className="w-full px-4 py-3 rounded-xl border bg-gray-50 border-gray-200 text-gray-900 focus:border-[#FF7A59] focus:ring-2 focus:ring-[#FF7A59]/20 outline-none transition-all font-semibold" placeholder="e.g. 500001" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 ml-1">Alt. Phone Number</label>
                <input type="tel" name="alternatePhoneNumber" value={formData.alternatePhoneNumber} onChange={handleChange} maxLength="10" className="w-full px-4 py-3 rounded-xl border bg-gray-50 border-gray-200 text-gray-900 focus:border-[#FF7A59] focus:ring-2 focus:ring-[#FF7A59]/20 outline-none transition-all font-semibold" placeholder="Optional" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 ml-1">City</label>
                <input type="text" name="city" value={formData.city} onChange={handleChange} className="w-full px-4 py-3 rounded-xl border bg-gray-100 border-gray-200 outline-none font-semibold text-gray-600" placeholder="Auto-fetched" readOnly />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 ml-1">State</label>
                <input type="text" name="state" value={formData.state} onChange={handleChange} className="w-full px-4 py-3 rounded-xl border bg-gray-100 border-gray-200 outline-none font-semibold text-gray-600" placeholder="Auto-fetched" readOnly />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 ml-1">House / Flat No *</label>
                <input type="text" name="houseNumber" value={formData.houseNumber} onChange={handleChange} className="w-full px-4 py-3 rounded-xl border bg-gray-50 border-gray-200 text-gray-900 focus:border-[#FF7A59] focus:ring-2 focus:ring-[#FF7A59]/20 outline-none transition-all font-semibold" placeholder="House No." />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 ml-1">Apartment / Building</label>
                <input type="text" name="apartmentName" value={formData.apartmentName} onChange={handleChange} className="w-full px-4 py-3 rounded-xl border bg-gray-50 border-gray-200 text-gray-900 focus:border-[#FF7A59] focus:ring-2 focus:ring-[#FF7A59]/20 outline-none transition-all font-semibold" placeholder="Building Name" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 ml-1">Street / Area *</label>
              <input type="text" name="streetName" value={formData.streetName} onChange={handleChange} className="w-full px-4 py-3 rounded-xl border bg-gray-50 border-gray-200 text-gray-900 focus:border-[#FF7A59] focus:ring-2 focus:ring-[#FF7A59]/20 outline-none transition-all font-semibold" placeholder="Street details" />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 ml-1">Landmark</label>
              <input type="text" name="landMark" value={formData.landMark} onChange={handleChange} className="w-full px-4 py-3 rounded-xl border bg-gray-50 border-gray-200 text-gray-900 focus:border-[#FF7A59] focus:ring-2 focus:ring-[#FF7A59]/20 outline-none transition-all font-semibold" placeholder="Famous place nearby" />
            </div>

            <div className="pt-2">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 ml-1">Address Type</label>
              <div className="flex gap-3">
                {/* FIXED: Changed enums to exactly match Backend: HOME, OFFICE, OTHERS */}
                <button type="button" onClick={() => setFormData({...formData, addressType: 'HOME'})} className={`flex-1 py-2.5 rounded-xl text-xs sm:text-sm font-bold border-2 transition-all flex items-center justify-center gap-2 ${formData.addressType === 'HOME' ? 'border-[#0F2F63] bg-sky-50 text-[#0F2F63]' : 'border-gray-100 bg-white text-gray-500'}`}>
                  <i className="fas fa-home"></i> Home
                </button>
                <button type="button" onClick={() => setFormData({...formData, addressType: 'OFFICE'})} className={`flex-1 py-2.5 rounded-xl text-xs sm:text-sm font-bold border-2 transition-all flex items-center justify-center gap-2 ${formData.addressType === 'OFFICE' ? 'border-[#0F2F63] bg-sky-50 text-[#0F2F63]' : 'border-gray-100 bg-white text-gray-500'}`}>
                  <i className="fas fa-briefcase"></i> Office
                </button>
                <button type="button" onClick={() => setFormData({...formData, addressType: 'OTHERS'})} className={`flex-1 py-2.5 rounded-xl text-xs sm:text-sm font-bold border-2 transition-all flex items-center justify-center gap-2 ${formData.addressType === 'OTHERS' ? 'border-[#0F2F63] bg-sky-50 text-[#0F2F63]' : 'border-gray-100 bg-white text-gray-500'}`}>
                  <i className="fas fa-map-marker-alt"></i> Others
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-3 ml-1">
              <input type="checkbox" id="isPrimary" name="isPrimary" checked={formData.isPrimary} onChange={handleChange} className="w-4 h-4 text-[#FF7A59] rounded border-gray-300 focus:ring-[#FF7A59]" />
              <label htmlFor="isPrimary" className="text-sm font-bold text-gray-700 cursor-pointer">Set as default address</label>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-gray-100 bg-white flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 py-3.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-black transition-colors">Cancel</button>
          <button type="submit" form="address-form" disabled={loading} className="flex-1 py-3.5 bg-[#0F2F63] hover:bg-[#1a4a8a] text-white rounded-xl font-black transition-colors shadow-lg disabled:opacity-70 flex items-center justify-center gap-2">
            {loading ? <i className="fas fa-spinner fa-spin"></i> : <><i className="fas fa-save"></i> Save Address</>}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddressModal;